/**
 * @file        src/components/ResponsiveImage.tsx
 * @description Shared responsive-image renderer for content photos: a native <picture> with AVIF + WebP
 *              srcset sources (built from the imageVariants SoT) and a JPEG-master <img> fallback,
 *              degrading to a bare <img src={master}> when variant metadata is absent. The single render
 *              seam every site image surface uses (catalog now, news/blog later).
 * @rule        I8 — no optimizer: this is a RAW <picture>/<img>. It must NEVER import next/image or emit
 *              a /_next/image URL (that on-demand path is exactly what broke on newly-added files). Stay
 *              PURE and hookless (no "use client", no state/effects) so it works in server AND client
 *              components. srcset strings come ONLY from buildPictureSources — never hand-built here.
 * @rule        I9 — no CLS: `fill` replicates next/image fill layout exactly (absolute inset-0 h-full
 *              w-full; the positioned parent supplies the aspect box); non-fill carries width/height.
 * @universal   knowledge/universals/components.md#ResponsiveImage
 */

import { cn } from '@/lib/utils';
import { buildPictureSources } from '@/lib/imageVariants';

/** Props for {@link ResponsiveImage}. A drop-in for the car-photo `<Image ... unoptimized>` prop subset,
 *  with next/image `src` replaced by `masterUrl` + `intrinsicWidth`. */
export interface ResponsiveImageProps {
  /** JPEG master path — a `car.photos[i]` value, e.g. "/storage/catalog/{slug}/1.jpg". */
  masterUrl: string;
  /** The master's real pixel width — a `Car.photoWidths[i]` value. undefined or 0 → bare-<img> fallback. */
  intrinsicWidth?: number;
  /** Alt text. */
  alt: string;
  /** The per-slot `sizes` attribute (e.g. "112px" or "(max-width: 768px) 100vw, 60vw"). */
  sizes: string;
  /** When true, the <img> fills its positioned parent (absolute inset-0 h-full w-full; no width/height). */
  fill?: boolean;
  /** Explicit intrinsic width — used only when NOT fill (no CLS). */
  width?: number;
  /** Explicit intrinsic height — used only when NOT fill (no CLS). */
  height?: number;
  /** true → loading="eager" + fetchPriority="high"; false/undefined → loading="lazy". Always decoding="async". */
  priority?: boolean;
  /** Classes for the <img> — object-cover/object-contain and any layout classes. */
  className?: string;
}

/**
 * Render a car/content photo responsively without the Next.js optimizer (I8).
 *
 * Two branches:
 *  - variants present (`buildPictureSources` non-null) → <picture> with image/avif + image/webp
 *    <source srcSet sizes> and a JPEG-master <img> (the <img> carries NO sizes/srcSet — those live on
 *    the <source> elements that actually have a srcset).
 *  - variants absent (intrinsicWidth undefined/0) → a bare <img src={masterUrl}> (the common path until
 *    the step-06 backfill runs), so an un-backfilled photo still shows its master, never a broken tile.
 */
export function ResponsiveImage({
  masterUrl,
  intrinsicWidth,
  alt,
  sizes,
  fill,
  width,
  height,
  priority,
  className,
}: ResponsiveImageProps) {
  const sources = buildPictureSources(masterUrl, intrinsicWidth ?? 0);

  const loading = priority ? 'eager' : 'lazy';
  const fetchPriority = priority ? 'high' : undefined;
  const imgClass = fill ? cn('absolute inset-0 h-full w-full', className) : className;
  const dims = fill ? {} : { width, height };

  // Graceful fallback: no variant metadata → a single bare master, no <picture>, no srcset/sizes.
  if (sources === null) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={masterUrl}
        alt={alt}
        className={imgClass}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        {...dims}
      />
    );
  }

  return (
    <picture>
      <source type="image/avif" srcSet={sources.avif} sizes={sizes} />
      <source type="image/webp" srcSet={sources.webp} sizes={sizes} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={sources.jpeg}
        alt={alt}
        className={imgClass}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        {...dims}
      />
    </picture>
  );
}
